import { c } from '../../theme';
import ArcDial, { TONE, fillTowards, fillUnder } from './ArcDial';

// The old dashboard's gauge as a CARD: the three-quarter arc with the figure
// in it, a sublabel, the goal and how far off it the figure sits.
//
// The arc itself now lives in ArcDial, because the Board needs the same
// drawing inside a different card — one that also carries a target, a change,
// a year to date and a bar per month. Everything about the geometry, and why
// each number in it was measured rather than matched by eye, is documented
// there.
//
// This file is the VP and Team pages' card. It renders exactly what it did
// before the arc was extracted.

export default function ArcGauge({
  title, value, display, sublabel, tone, fill, goalFraction, goalLabel, goalText, beat, footnote,
}) {
  const beatColour = beat == null ? c.textMuted : beat >= 0 ? TONE.good : TONE.bad;

  // Six slots, always, in this order — an empty one renders as an empty div.
  // The card is a SUBGRID: its rows are the parent grid's rows, shared with
  // every other card in the same row of the page, so each slot is as tall as
  // the tallest card's version of it. A title that wraps to two lines (or three:
  // "Service level · Oct 1, 2026 – Oct 1, 2026" on a narrow card) then pushes
  // every arc in the row down together instead of only its own. Leave a slot
  // out when it is empty and the next one moves up into its row — the goal line
  // of a card with no sublabel would sit in the sublabel row.
  return (
    <section style={s.card} aria-label={title}>
      <div style={s.title}>{title}</div>
      <div>
        <ArcDial value={value} display={display} tone={tone} fill={fill}
          goalFraction={goalFraction} goalLabel={goalLabel}
          ariaLabel={`${display}${goalText ? `, ${goalText}` : ''}`} />
      </div>
      {/* The sublabel sits BELOW the arc, in HTML, not inside the SVG.
          Drawn at y = CY + 22 it was 22 units under the centre, where the arc
          is still 72 units wide either side — so anything longer than about
          "Voice + VIP" ran straight through the stroke. SVG text does not
          wrap and does not shrink, so the slot cannot be made safe; the only
          fix that holds for a string nobody has thought of yet is to take it
          out of the drawing. `Inbound + callbacks + VIP` was the one that
          found this. */}
      {sublabel ? <div data-testid="gauge-sublabel" style={s.sublabel}>{sublabel}</div> : <div aria-hidden="true" />}
      {goalText ? (
        <div style={s.goal}>
          <span style={{ ...s.dot, background: beat == null ? TONE.neutral : beatColour }} />
          {goalText}
        </div>
      ) : <div aria-hidden="true" />}
      {beat != null ? (
        <div style={{ ...s.beat, color: beatColour }}>
          {beat >= 0 ? '▲' : '▼'} {Math.abs(beat)}% {beat >= 0 ? 'above' : 'below'} goal
        </div>
      ) : <div aria-hidden="true" />}
      {footnote ? <div style={s.footnote}>{footnote}</div> : <div aria-hidden="true" />}
    </section>
  );
}

/** How many rows of the page's grid a card spans: one per slot above. */
export const GAUGE_SLOTS = 6;

// Re-exported so the pages that import them from here keep working; they are
// defined with the geometry they belong to.
export { fillTowards, fillUnder };

const s = {
  // Subgrid where the browser has it; where it does not, gridTemplateRows is
  // ignored and the card is a plain one-column stack, exactly as before —
  // nothing breaks, the rows just stop lining up across cards.
  card:     { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px',
    padding: '12px 10px 10px', display: 'grid', gridTemplateRows: 'subgrid', gridRow: `span ${GAUGE_SLOTS}`,
    rowGap: 0, alignContent: 'start', textAlign: 'center', minWidth: 0 },
  title:    { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: c.textMuted, textAlign: 'center', marginBottom: '2px' },
  sublabel: { fontSize: '11.5px', fontWeight: 500, color: c.textMuted, textAlign: 'center',
    lineHeight: 1.3, marginTop: '1px', maxWidth: '100%' },
  goal:     { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', fontSize: '11px',
    fontWeight: 500, color: c.textMuted, fontVariantNumeric: 'tabular-nums', marginTop: '2px' },
  dot:      { width: '6px', height: '6px', borderRadius: '999px', flexShrink: 0 },
  beat:     { fontSize: '11px', fontWeight: 500, fontVariantNumeric: 'tabular-nums', marginTop: '2px' },
  footnote: { fontSize: '10px', color: c.textFaint, textAlign: 'center', marginTop: '3px', lineHeight: 1.3 },
};
